import React, { useState } from 'react'
import type { ContextUploadFileItem } from './message'
import {
  getFileExtension,
  getFileNameWithoutExtension,
  CARD_STYLES,
  CARD_SIZES
} from './uploadCardShared'
import { getFileTypeIconByFilename, GenericFolderIcon } from '~/utils/fileTypeIcons'
import { getContextUploadDownloadUrl } from '~/services/organizationService'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { BetterToast } from '~/utils/toast'

export interface MessageUploadCardProps {
  upload: ContextUploadFileItem
}

/**
 * Clickable upload card for showing context uploads above user messages.
 * Clicking downloads the file from S3 via presigned URL.
 * Figma specs: 192x64px, padding: 16px, rounded 16px
 */
export const MessageUploadCard: React.FC<MessageUploadCardProps> = ({ upload }) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const { currentWorkspace } = useWorkspaceContext()

  const isDirectory = !!upload.directory_upload
  const name = isDirectory ? upload.directory_upload!.directory_name : upload.file_upload!.file_name
  const s3Path = isDirectory ? upload.directory_upload!.s3_path : upload.file_upload!.s3_path
  const fileExtension = isDirectory ? 'folder' : getFileExtension(name)
  const displayName = getFileNameWithoutExtension(name, isDirectory)

  const handleClick = async () => {
    if (isDownloading || !currentWorkspace?.uuid || !s3Path) return

    setIsDownloading(true)
    try {
      const { url, filename } = await getContextUploadDownloadUrl(currentWorkspace.uuid, s3Path)

      // Trigger download using the presigned URL
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to download file')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div
      onClick={handleClick}
      className={`flex items-center overflow-hidden cursor-pointer transition-opacity ${
        isDownloading ? 'opacity-60' : 'hover:opacity-80'
      }`}
      style={{
        width: `${CARD_SIZES.message.width}px`,
        height: `${CARD_SIZES.message.height}px`,
        borderRadius: CARD_STYLES.borderRadius,
        padding: '16px',
        gap: '8px',
        background: CARD_STYLES.background,
        boxShadow: CARD_STYLES.shadow
      }}
      title={`Click to download ${name}`}
    >
      {/* Content: File name + type info */}
      <div className='flex flex-col gap-1 min-w-0 flex-1 justify-center'>
        {/* File/Folder Name */}
        <div
          className='font-medium truncate overflow-hidden text-ellipsis whitespace-nowrap w-full'
          style={{
            fontSize: '12px',
            lineHeight: '16px',
            color: CARD_STYLES.textPrimary,
            fontFamily: 'Geist, sans-serif'
          }}
          title={name}
        >
          {displayName}
        </div>

        {/* File type info: icon + extension */}
        <div className='flex items-center gap-1'>
          <span
            className='flex items-center justify-center'
            style={{ width: '16px', height: '16px', color: CARD_STYLES.iconColor }}
          >
            {isDirectory ? (
              <GenericFolderIcon />
            ) : (
              (() => {
                const IconComponent = getFileTypeIconByFilename(name)
                return <IconComponent />
              })()
            )}
          </span>
          <span
            className='font-semibold'
            style={{
              fontFamily: 'Geist Mono, monospace',
              fontSize: '12px',
              lineHeight: '100%',
              color: CARD_STYLES.textSecondary
            }}
          >
            {fileExtension}
          </span>
        </div>
      </div>
    </div>
  )
}

export default MessageUploadCard
